/**
 * Codex at-capacity contracts (#2575).
 *
 * ── WHAT "AT-CAPACITY" IS, AND WHY THIS FILE DOES NOT REDEFINE IT ──────────
 * OpenAI/codex writes capacity refusal into an HTTP 200 SSE
 * (`server_is_overloaded`). The gateway records that as `usage_logs` 2xx +
 * `billing_outcome = 'zero_cost'` + four token columns all 0. The tenant
 * sees an empty stream, which is a failure. That predicate is owned by
 * ndt-patrol (`usage_outcome.empty_2xx_sql` / `fast_checks._atcap_sql`).
 * The SQL in gateway-worker copies it. Do not invent a second definition
 * here — a ratio computed from a different WHERE is a different product.
 *
 * ── WHY AN ARCHIVE TABLE AT ALL ────────────────────────────────────────────
 * A 24-hour × 15-minute aggregation over live `usage_logs` is ~42 ms.
 * The same query at 21 days is ~38 s. The tenant page and the alert
 * evaluator both need a 24-hour window, so the window is pinned at 24
 * hours and the buckets live in a platform-owned archive. Gateway is the
 * only reader of `usage_logs` (architecture rule: no new cross-owner
 * reads). Control-loops copies the public-status rollup shape: gateway
 * aggregates, platform upserts.
 *
 * ── TENANT WIRE: RATIO IN, COUNTS OUT ──────────────────────────────────────
 * {@link TenantCodexCapacityResponse} and every type it contains
 * deliberately omit request-count fields. The ratio is the product; the
 * underlying 2xx volume is not. Returning `null` (not `0`) when a bucket
 * or the 24-hour window is under the sample floor is both noise control
 * and a block against inverting the ratio to recover volume. Do not add
 * `requests_2xx` / `at_capacity` / any synonym onto these types.
 *
 * ── BUCKET TIMESTAMPS ARE A PAIR, UTC Z, NO SESSION TZ ─────────────────────
 * Each point carries `t_start` and `t_end` (`t_end = t_start + 15 min`).
 * Both are UTC ISO-8601 with a trailing `Z` and no fractional seconds
 * (`2026-09-21T09:15:00Z`). Production RDS sessions are Asia/Tokyo; a
 * `timestamptz::text` cast follows the session, so the same row would
 * render differently in tests vs production. Format in TypeScript with
 * {@link formatUtcIsoZ} (Date#toISOString is always UTC).
 */

export const CODEX_CAPACITY_LANE_CHANNEL_SLUG = 'codex';
export const CODEX_CAPACITY_BUCKET_MINUTES = 15;
export const CODEX_CAPACITY_WINDOW_HOURS = 24;
/** 24h / 15min. Fixed length of every tenant series; missing archive rows are `ratio: null`. */
export const CODEX_CAPACITY_WINDOW_BUCKET_COUNT =
  (CODEX_CAPACITY_WINDOW_HOURS * 60) / CODEX_CAPACITY_BUCKET_MINUTES;
/** A bucket with fewer 2xx than this does not get a ratio (`null`) — noise floor and anti-inversion. */
export const CODEX_CAPACITY_MIN_BUCKET_SAMPLE = 20;
/** 24-hour rollup ratio's minimum 2xx sample. */
export const CODEX_CAPACITY_MIN_WINDOW_SAMPLE = 100;
/**
 * One rollup pass may look back at most this many minutes. An operator
 * (or a buggy caller) passing 10000 would scan live `usage_logs` across
 * days; production measured 38 s at 21 days.
 */
export const CODEX_CAPACITY_MAX_LOOKBACK_MINUTES = 360;
export const CODEX_CAPACITY_MAX_AUDIENCE = 5000;
export const CODEX_CAPACITY_DEFAULT_LOOKBACK_MINUTES = 60;
export const CODEX_CAPACITY_DEFAULT_AUDIENCE_DAYS = 7;
export const CODEX_CAPACITY_DEFAULT_RETENTION_HOURS = 26;
export const CODEX_CAPACITY_DEFAULT_FIRE_PCT = 30;
export const CODEX_CAPACITY_DEFAULT_CLEAR_PCT = 15;
export const CODEX_CAPACITY_DEFAULT_WINDOW_MINUTES = 120;
export const CODEX_CAPACITY_DEFAULT_CLEAR_WINDOW_MINUTES = 120;
export const CODEX_CAPACITY_DEFAULT_MIN_WINDOW_REQUESTS = 200;
export const CODEX_CAPACITY_DEFAULT_MAX_SENDS_PER_PASS = 100;
export const CODEX_CAPACITY_DEFAULT_COOLDOWN_HOURS = 0;
export const CODEX_CAPACITY_DEFAULT_QUIET_HOURS_TZ = 'Asia/Shanghai';
export const CODEX_CAPACITY_AUDIENCE_DAYS_MIN = 1;
export const CODEX_CAPACITY_AUDIENCE_DAYS_MAX = 30;
/** 0084 CHECK: window_minutes / clear_window_minutes / rollup_lookback_minutes. */
export const CODEX_CAPACITY_WINDOW_MINUTES_MIN = 15;
/** 0084 CHECK: retention_hours. */
export const CODEX_CAPACITY_RETENTION_HOURS_MIN = 24;
/**
 * Observation and recover windows must fit inside the archive. Ingest
 * prunes buckets older than `retention_hours`; a longer window would
 * silently sum a truncated archive.
 */
export const CODEX_CAPACITY_RETENTION_MINUTES_PER_HOUR = 60;
/**
 * Archive is stale when the newest bucket_start or updated_at is more
 * than this many buckets behind `now`. One bucket of slack covers the
 * in-progress 15-minute UTC slot; missing the slot before that means
 * rollup has stopped.
 */
export const CODEX_CAPACITY_ARCHIVE_STALE_BUCKETS = 2;
/** 0084 CHECK: fire_pct. */
export const CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE = 0;
export const CODEX_CAPACITY_FIRE_PCT_MAX = 100;
/** 0084 CHECK: clear_pct. */
export const CODEX_CAPACITY_CLEAR_PCT_MIN = 0;
/** 0084 CHECK: quiet hour fields. */
export const CODEX_CAPACITY_QUIET_HOUR_MIN = 0;
export const CODEX_CAPACITY_QUIET_HOUR_MAX = 23;
export const CODEX_CAPACITY_ALERT_HISTORY_DEFAULT_LIMIT = 100;
export const CODEX_CAPACITY_ALERT_HISTORY_MAX_LIMIT = 500;
/**
 * Pass-lease TTL for `codex_capacity_alert_state.pass_lease_until`.
 *
 * One pass mails at most `max_sends_per_pass` (100) serial SMTP sends.
 * Control-loops' client budget is 150s and the handler kill is 180s; the
 * platform route is not cancelled when the client gives up, so the lease
 * has to outlive that kill. 180s of send + 30s of SQL/audience + 30s of
 * slack = 240s. After that the column is stale and the next pass may
 * take over without an operator clearing it.
 */
export const CODEX_CAPACITY_ALERT_PASS_LEASE_MS = 240_000;
/** History rows from `/test-send` carry this incident_id; they never open a real incident. */
export const CODEX_CAPACITY_TEST_SEND_INCIDENT_ID = 'test_send';

const ISO_Z_WITHOUT_FRACTION = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

export function formatUtcIsoZ(value: Date): string {
  return value.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function isUtcIsoZ(value: string): boolean {
  return ISO_Z_WITHOUT_FRACTION.test(value) && Number.isFinite(Date.parse(value));
}

export function floorToCodexCapacityBucketMs(ms: number): number {
  const bucketMs = CODEX_CAPACITY_BUCKET_MINUTES * 60 * 1000;
  return Math.floor(ms / bucketMs) * bucketMs;
}

export function isCodexCapacityArchiveStale(input: {
  readonly nowMs: number;
  readonly latestBucketStartMs: number | null;
  readonly latestUpdatedAtMs: number | null;
}): boolean {
  if (input.latestBucketStartMs === null || input.latestUpdatedAtMs === null) return true;
  const limitMs =
    CODEX_CAPACITY_ARCHIVE_STALE_BUCKETS * CODEX_CAPACITY_BUCKET_MINUTES * 60 * 1000;
  return (
    input.nowMs - input.latestBucketStartMs > limitMs
    || input.nowMs - input.latestUpdatedAtMs > limitMs
  );
}

export function clampCodexCapacityLookbackMinutes(raw: number | undefined): number {
  if (raw === undefined || !Number.isFinite(raw)) {
    return CODEX_CAPACITY_DEFAULT_LOOKBACK_MINUTES;
  }
  const minutes = Math.floor(raw);
  if (minutes < CODEX_CAPACITY_BUCKET_MINUTES) return CODEX_CAPACITY_BUCKET_MINUTES;
  if (minutes > CODEX_CAPACITY_MAX_LOOKBACK_MINUTES) return CODEX_CAPACITY_MAX_LOOKBACK_MINUTES;
  return minutes;
}

export function clampCodexCapacityAudienceDays(raw: number | undefined): number {
  if (raw === undefined || !Number.isFinite(raw)) {
    return CODEX_CAPACITY_DEFAULT_AUDIENCE_DAYS;
  }
  const days = Math.floor(raw);
  if (days < CODEX_CAPACITY_AUDIENCE_DAYS_MIN) return CODEX_CAPACITY_AUDIENCE_DAYS_MIN;
  if (days > CODEX_CAPACITY_AUDIENCE_DAYS_MAX) return CODEX_CAPACITY_AUDIENCE_DAYS_MAX;
  return days;
}

/**
 * Align the rollup window.
 *
 * `window_end` is `now`. `window_start` is `now - lookback`, floored onto a
 * 15-minute UTC boundary. The lookback is already clamped to >= one bucket,
 * so the aligned window is at least one bucket wide.
 *
 * Recomputing several recent buckets (not only the in-progress one) is
 * load-bearing: CN edge traffic reaches Tokyo through uplink replication
 * with lag. Finalising the current bucket on first sight permanently
 * drops the CN volume that arrives a few minutes later.
 */
export function codexCapacityRollupWindow(
  nowMs: number,
  lookbackMinutes: number,
): { readonly windowStartMs: number; readonly windowEndMs: number } {
  const bucketMs = CODEX_CAPACITY_BUCKET_MINUTES * 60 * 1000;
  const windowEndMs = nowMs;
  let windowStartMs = floorToCodexCapacityBucketMs(nowMs - lookbackMinutes * 60 * 1000);
  if (windowEndMs - windowStartMs < bucketMs) {
    windowStartMs = floorToCodexCapacityBucketMs(windowEndMs - bucketMs);
  }
  return { windowStartMs, windowEndMs };
}

export interface CodexCapacityRollupLookbackResponse {
  readonly lookback_minutes: number;
}

export interface CodexCapacityRollupRunRequest {
  readonly lookbackMinutes?: number;
}

export interface CodexCapacityRollupRow {
  readonly bucket_start: string;
  readonly sku_name: string;
  readonly requests_2xx: number;
  readonly at_capacity: number;
}

export interface CodexCapacityRollupRunResponse {
  readonly bucket_minutes: number;
  readonly window_start: string;
  readonly window_end: string;
  readonly rows: readonly CodexCapacityRollupRow[];
}

export interface CodexCapacityRollupIngestResponse {
  readonly ok: boolean;
  readonly buckets_written: number;
  readonly buckets_pruned: number;
  readonly window_start: string;
  readonly window_end: string;
  readonly error?: string;
}

export interface CodexCapacityAudienceResponse {
  readonly days: number;
  readonly account_ids: readonly string[];
  readonly truncated: boolean;
}

export type CodexCapacityState = 'normal' | 'elevated' | 'high';

/**
 * One 15-minute point on a tenant series.
 *
 * `t_start` / `t_end` are UTC ISO-8601 with a trailing `Z` and no
 * fractional seconds. The tenant UI renders them in the user's local
 * timezone; the API never emits a session-timezone string.
 */
export interface CodexCapacityBucketPoint {
  /** Bucket start, UTC ISO-8601 (`2026-09-21T09:15:00Z`). */
  readonly t_start: string;
  /** Bucket end = t_start + CODEX_CAPACITY_BUCKET_MINUTES, same format. */
  readonly t_end: string;
  /** 0–100, one decimal. `null` when the bucket is missing or under the sample floor. */
  readonly ratio: number | null;
}

export interface CodexCapacitySeries {
  readonly ratio_24h: number | null;
  readonly state: CodexCapacityState;
  readonly buckets: readonly CodexCapacityBucketPoint[];
}

export interface CodexCapacityModelSeries extends CodexCapacitySeries {
  readonly sku: string;
}

/**
 * Tenant-facing Codex capacity payload.
 *
 * No request-count fields live on this type or on any nested type. The
 * ratio is visible; the volume is not. Under-sampled windows return
 * `null` rather than `0` so a quiet period cannot be mistaken for a
 * healthy zero, and so `ratio * sample` cannot recover volume.
 */
export interface TenantCodexCapacityResponse {
  readonly window_hours: number;
  readonly bucket_minutes: number;
  readonly updated_at: string | null;
  readonly overall: CodexCapacitySeries;
  readonly by_model: readonly CodexCapacityModelSeries[];
}
